import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AiFeedbackButtons from '../../components/ai-copilot/AiFeedbackButtons';

const AiCopilotPage = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('Search chocolate cake products');
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [result, setResult] = useState(null);
  const [pendingApproval, setPendingApproval] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const runPrompt = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const response = await axios.post('ai/chat', { prompt });
      setResult(response.data);
      setPendingApproval(response.data?.pending_approval || null);
    } catch (error) {
      setResult(
        error?.response?.data || {
          success: false,
          message: 'Request failed',
          error: String(error),
        },
      );
      setPendingApproval(null);
    } finally {
      setLoading(false);
    }
  };

  const approveChange = async () => {
    if (!pendingApproval) return;
    setApproving(true);
    try {
      const response = await axios.post('ai/approve', {
        approval_token: pendingApproval.token,
      });
      setResult((prev) => ({
        ...(prev || {}),
        approval: response.data,
        approvalMessage: response.data.success
          ? `Approved and executed: ${response.data.result?.message || 'done'}`
          : response.data.message,
      }));
    } catch (error) {
      setResult((prev) => ({
        ...(prev || {}),
        approval: error?.response?.data || {
          success: false,
          message: 'Approval request failed',
          error: String(error),
        },
        approvalMessage: error?.response?.data?.message || 'Approval request failed',
      }));
    } finally {
      setPendingApproval(null);
      setApproving(false);
    }
  };

  const dismissApproval = () => {
    setPendingApproval(null);
  };

  const rateResult = async (rating, comment) => {
    if (!result?.request_id) return;
    setFeedback(rating);
    try {
      await axios.post('ai/feedback', {
        request_id: result.request_id,
        rating,
        comment,
        response_text: result.response,
        module: result.routed_module || 'auto',
      });
    } catch (error) {
      setFeedback(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h5">AI Admin Copilot</Typography>

            <TextField
              label="Ask the AI"
              multiline
              minRows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />

            <Button variant="contained" onClick={runPrompt} disabled={loading || approving}>
              {loading ? <CircularProgress size={20} /> : 'Run AI'}
            </Button>

            {result && (
              <Box sx={(theme) => ({
                p: 2, bgcolor: 'background.paper', borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                '& table': { borderCollapse: 'collapse', width: '100%', my: 1 },
                '& th, & td': { border: `1px solid ${theme.palette.divider}`, px: 1.5, py: 0.5, textAlign: 'left' },
                '& th': { bgcolor: 'action.hover', fontWeight: 'bold' },
                '& h1, & h2, & h3': { mt: 2, mb: 1 },
                '& hr': { my: 2, borderColor: 'divider' },
                '& p': { my: 1 },
                '& ul, & ol': { pl: 3, my: 1 },
              })}>                {result.success && result.response ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.response}</ReactMarkdown>
                ) : (
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                    {JSON.stringify(result, null, 2)}
                  </pre>
                )}
              </Box>
            )}

            {result?.success && result?.response && (
              <AiFeedbackButtons
                requestId={result.request_id}
                value={feedback}
                onRate={rateResult}
              />
            )}

            {result?.suggested_action && (
              <Card variant="outlined" sx={{ bgcolor: '#e8f5e9', borderColor: '#4caf50', mt: 2 }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight="bold" color="#2e7d32">
                      Quick Action
                    </Typography>
                    <Typography variant="body2">
                      {result.suggested_action.description}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => navigate(result.suggested_action.url)}
                        size="small"
                      >
                        {result.suggested_action.label}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {pendingApproval && (
              <Card variant="outlined" sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? '#2d1f00' : '#fff8e1', borderColor: '#f9a825' })}>
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" fontWeight="bold" color="#e65100">
                      Approval required
                    </Typography>
                    <Typography variant="body2">
                      {pendingApproval.summary || pendingApproval.tool_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      This change will not run until you approve it. Expires{' '}
                      {pendingApproval.expires_at
                        ? new Date(pendingApproval.expires_at).toLocaleString()
                        : 'soon'}
                      .
                    </Typography>
                    {result?.approvalMessage && (
                      <Typography variant="body2" color={result.approval?.success ? 'success.main' : 'error'}>
                        {result.approvalMessage}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={approveChange}
                        disabled={approving}
                      >
                        {approving ? <CircularProgress size={20} /> : 'Approve'}
                      </Button>
                      <Button variant="outlined" onClick={dismissApproval} disabled={approving}>
                        Dismiss
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AiCopilotPage;